import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import StorageIcon from '@mui/icons-material/Storage';
import PublicIcon from '@mui/icons-material/Public';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LayersIcon from '@mui/icons-material/Layers';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import { CostFilterDto, DashboardTab, MergeOptions } from '../Model/CostType';
import { useCostSummary } from '../hooks/UseCostSummary';
import { useMergedTenants } from '../hooks/Usemergedtenants';
import { useTrendData } from '../hooks/Usetrenddata';
import { buildTabs } from '../Model/Constants';
import TenantsTab from '../Components/TenantsTab';
import ScopeTab from '../Components/ScopeTab';
import FarmsTab from '../Components/FarmsTab';
import OrphansTab from '../Components/OrphansTab';
import TrendTab from '../Components/TrendTab';
import ServiceTypeTab from '../Components/ServiceTypeTab';
import KpiCard from '../Components/KpiCard';
import TabBar from '../Components/Tabber';
import MergeControls from '../Components/MergeControl';
import {
  fmtCompact,
  USD,
  NOK,
  CurrencyConfig,
} from '../../../Components/Layout/SqlServer/Common/util';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import DownloadIcon from '@mui/icons-material/Download';
import Tooltip from '@mui/material/Tooltip';
import { exportAllTenantsCsv, refreshData } from '../Services/CostService';
import Snackbar from '@mui/material/Snackbar';

function defaultFilter(): CostFilterDto {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { From: fmt(from), To: fmt(to) };
}

const CostDashboard: React.FC = () => {
  const [filter] = useState<CostFilterDto>(defaultFilter);
  const [activeTab, setActiveTab] = useState<DashboardTab>('tenants');
  const [showVirtual, setShowVirtual] = useState(true);
  const [currency, setCurrency] = useState<CurrencyConfig>(NOK);
  const [mergeOptions, setMergeOptions] = useState<MergeOptions>({
    FarmShared: false,
    RegionalShared: false,
    Platform: false,
  });
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string }>({
    open: false,
    msg: '',
  });

  const { summary, orphans, loading, error, refetch } = useCostSummary(filter);
  const mergedTenants = useMergedTenants(
    summary?.TenantSummary ?? [],
    mergeOptions,
    showVirtual,
  );
  const trendChartData = useTrendData(summary?.Trend ?? []);
  const tabs = buildTabs(orphans.length);

  const realTenants = useMemo(
    () => (summary?.TenantSummary ?? []).filter((t) => !t.IsVirtual),
    [summary],
  );
  const realTenantCost = realTenants.reduce((s, t) => s + t.TotalCost, 0);
  const sharedCost =
    summary?.TenantSummary.find((t) => t.IsShared)?.TotalCost ?? 0;
  const platformCost =
    summary?.TenantSummary.find((t) => t.IsPlatform)?.TotalCost ?? 0;
  const unknownPrior =
    summary?.TenantSummary.find((t) => t.IsUnknown)?.LastPeriodCost ?? 0;
  const unknownCost = summary?.UntaggedCost ?? 0;

  const baseCurrency = summary?.Currency ?? 'NOK';

  const handleRefresh = async () => {
    if (
      !window.confirm(
        'WARNING: This will delete ALL current cost data and start a 60-day re-ingestion from Azure. This process takes 5-10 minutes. Proceed?',
      )
    )
      return;

    setRefreshing(true);
    try {
      const res = await refreshData(60);
      setSnackbar({
        open: true,
        msg: `Refresh started! (Job: ${res.jobId}). Data will populate shortly.`,
      });
      // Optionally trigger a local refetch after some delay or keep it as is
    } catch (err) {
      setSnackbar({ open: true, msg: 'Failed to initiate refresh.' });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={36} />
          <Typography
            sx={{
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'text.secondary',
            }}
          >
            Fetching cost data from Azure…
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {filter.From} → {filter.To}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            {error}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refetch}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  const renderTab = () => {
    if (!summary) return null;
    switch (activeTab) {
      case 'tenants':
        return (
          <TenantsTab
            rows={mergedTenants}
            filter={filter}
            currency={currency}
            baseCurrency={baseCurrency}
          />
        );
      case 'scope':
        return (
          <ScopeTab
            scopeSummary={summary.ScopeSummary}
            currency={currency}
            baseCurrency={baseCurrency}
          />
        );
      case 'farms':
        return (
          <FarmsTab
            farmSummary={summary.FarmSummary}
            currency={currency}
            baseCurrency={baseCurrency}
          />
        );
      case 'servicetypes':
        return (
          <ServiceTypeTab
            serviceTypeSummary={summary.ServiceTypeSummary}
            currency={currency}
            baseCurrency={baseCurrency}
            filter={{ from: filter.From, to: filter.To }}
          />
        );
      case 'trend':
        return (
          <TrendTab
            dailyData={trendChartData}
            monthlyData={summary.MonthlyTrend}
            currency={currency}
            baseCurrency={baseCurrency}
          />
        );
      case 'orphans':
        return (
          <OrphansTab
            filter={{ from: filter.From, to: filter.To }}
            currency={currency}
            baseCurrency={baseCurrency}
          />
        );
    }
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        pt: 0,
        pb: 4,
        px: { xs: 2, md: 3 },
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}
          >
            Cost Executive Dashboard
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 600 }}
            >
              {filter.From} — {filter.To}
            </Typography>
            <Box
              sx={{
                px: 1,
                py: 0.25,
                bgcolor: summary?.Reconciliation.IsBalanced
                  ? 'rgba(34,197,94,0.1)'
                  : 'rgba(239,68,68,0.1)',
                border: `1px solid ${summary?.Reconciliation.IsBalanced ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: summary?.Reconciliation.IsBalanced
                    ? '#22c55e'
                    : '#ef4444',
                }}
              />
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: summary?.Reconciliation.IsBalanced
                    ? '#4ade80'
                    : '#f87171',
                }}
              >
                {summary?.Reconciliation.IsBalanced
                  ? 'RECONCILED'
                  : `DRIFT: ${fmtCompact(summary?.Reconciliation.Drift ?? 0, currency, baseCurrency)}`}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="Full Clear & Re-ingest (Last 60 Days)">
            <Button
              variant="outlined"
              size="small"
              color="warning"
              startIcon={
                refreshing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                borderColor: 'rgba(239,68,68,0.3)',
                color: 'error.main',
                '&:hover': {
                  borderColor: 'error.main',
                  bgcolor: 'rgba(239,68,68,0.05)',
                },
              }}
            >
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </Button>
          </Tooltip>

          <Tooltip title="Download Master Tenant Report (Summary)">
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => exportAllTenantsCsv(filter.From, filter.To)}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
            >
              Export All Tenants
            </Button>
          </Tooltip>
          <ToggleButtonGroup
            value={currency.code}
            exclusive
            onChange={(event, val) =>
              val && setCurrency(val === 'USD' ? USD : NOK)
            }
            size="small"
            sx={{
              bgcolor: 'background.paper',
              '& .MuiToggleButton-root': {
                px: 2,
                py: 0.5,
                fontSize: 11,
                fontWeight: 800,
                border: 'none',
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                },
              },
            }}
          >
            <ToggleButton value="USD">USD ($)</ToggleButton>
            <ToggleButton value="NOK">NOK (KR)</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} md={2}>
            <KpiCard
              title="Total Cost"
              value={fmtCompact(
                summary?.TotalCost ?? 0,
                currency,
                baseCurrency,
              )}
              icon={StorageIcon}
              color="primary"
              trendValue={summary?.DeltaPercent ?? 0}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <KpiCard
              title="Projected End"
              value={fmtCompact(
                summary?.ProjectedMonthEndCost ?? 0,
                currency,
                baseCurrency,
              )}
              icon={PublicIcon}
              color="info"
              sub="Est. Month-End"
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <KpiCard
              title="Tenant App"
              value={fmtCompact(realTenantCost, currency, baseCurrency)}
              icon={PublicIcon}
              color="info"
              sub={`${realTenants.length} tenants`}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard
              title="Shared"
              value={fmtCompact(sharedCost, currency, baseCurrency)}
              icon={AccountTreeIcon}
              color="warning"
              sub="Farm + Regional"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard
              title="Platform"
              value={fmtCompact(platformCost, currency, baseCurrency)}
              icon={LayersIcon}
              color="secondary"
              sub="Monitoring"
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <KpiCard
              title="Orphan"
              value={fmtCompact(unknownCost, currency, baseCurrency)}
              icon={WarningAmberIcon}
              color="error"
              sub={`${orphans.length} review needed`}
              trendValue={
                unknownPrior > 0
                  ? ((unknownCost - unknownPrior) / unknownPrior) * 100
                  : undefined
              }
            />
          </Grid>
        </Grid>

        <MergeControls
          options={mergeOptions}
          onOptionsChange={setMergeOptions}
          showVirtual={showVirtual}
          onShowVirtualChange={setShowVirtual}
        />

        <TabBar tabs={tabs} active={activeTab} onChange={setActiveTab} />

        <Box>{renderTab()}</Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.msg}
      />
    </Box>
  );
};

export default CostDashboard;
